import {ArticleIdentifier, QuestionnairePredefinedAnswer, QuestionnaireProcessingType, QuestionnaireQuestion, QuestionType, QuestionSubType, QuestionnairePageType} from "../../../store/Models/Surveys/SurveyDataModel";
import {DeclareCsiDivision, DeclareCsiDivisionsManager} from "../../../utilities/DeclareCsiDivision";
import {IStringLocalizer} from "../../../utilities/IStringLocalizer";
import ScipCategory, {CategoryType} from "../ArticleCategory";
import CsiDivision from "../CsiDivision";
import DateInput from "../DateInput";
import {DropdownMenu} from "../DropdownMenu";
import InputList from "../MultiText";
import TextAreaInput from "../TextAreaInput";
import TextInput from "../TextInput";
import UrlInput from "../UrlInput";
import RadioButtons, { IngredientBasedAnswerContext, PlausibilityDataAnswerContext } from "../RadioButtons";
import CheckBoxes from "../CheckBoxes";
import DataTable from "../DataTable";
import ScipArticleIdentifier from "../ArticleIdentifier";
import IngredientsTable from "../IngredientsTable";
import IngredientsTableColumnManager from "../../../utilities/IngredientsTableColumn";
import FileUploader from "../FileUploader";
import { FileMetadataType, FileTableEntry, FileTypeDetails } from "../../../store/Models/Documents/DocumentModels";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../../store";
import { InventoryQuestionnaireType, SurveyResponseState } from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import ComplianceResponseDate from "../ComplianceResponseDate";
import ComplianceResponseDateReach from "../ComplianceResponseDateReach/ComplianceResponseDateReach";
import {ReachListVersions} from "../../../application-layer/compliance/ReachListVersions";
import { RohsExemptions } from "../../../application-layer/compliance/RohsExemptions";
import CertificationInput from "../Certification";
import {Certification, CertificationType, PlausibilityData, SafeUseInstructionsScip} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import RevisionRequest from "../RevisionRequest/RevisionRequest";
import {BaseRegulationLogicManager} from "../../../store/Managers/BaseRegulationLogicManager";
import ResourcesConstants from "../../../store/Managers/ResourceConstants";
import {Characteristics} from "../Characteristics";
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import CheckboxAndComment from "../CheckboxAndComment/CheckboxAndComment";
import DropdownMenuAndComment from "../DropdownMenuAndComment/DropdownMenuAndComment";
import CMRTMineralsComponent from "../CMRTMineralsComponent/CMRTMineralsComponent";
import FDACitationsDataTable from "../../DataTables/FDACitationsDataTable/FDACitationsDataTable";
import PackagingDataTable from "../PackagingDataTable/PackagingDataTable";
import CustomDataTable from "../CustomDataTable/CustomDataTable";
import CmrtDetails from "../CmrtDetails";
import { CustomDataTableColumnProvider } from "../../../store/Managers/CustomDataTableColumnProvider";
import { AppServices } from "../../../AppServices";

import "./styles.css";
import ConflictMaterialsMultiMaterialInput from "../ConflictMaterialsMultiMaterialInput";
import PhoneContact from "../PhoneContact/PhoneContact";
import SmelterList from "../SmelterList/SmelterList";
import ProductList from "../ProductList/ProductList";
import ImportCMRT from "../ImportCMRT";
import AIAnswerMetadata from "../AiAnswerMetadata/AIAnswerMetadata";
import { ScipDataManager } from "../../../store/Managers/SurveyResponse/ScipDataManager";
import { normalizeJsonStringForComparison } from "../AiAnswerMetadata/aiAnswerMetadata.utils";
import {
    applyAiSoftCheckDecision,
    normalizeSoftCheckAnswer
} from "./softCheckContext.utils";

interface StateProps {
    coreData: SurveyResponseState["coreData"];
    selectedInventory: SurveyResponseState["selectedInventory"];
}

export interface QuestionOwnProps {
    questionIndex: number;
    displayIndex: number;
    parentDisplayIndex: number;
    question: QuestionnaireQuestion;
    onResponseChange?: (
        value: string,
        valueComment: string,
        questionIndex: number,
        files?: FileTableEntry[],
        attchedFileIds?: string[]
    ) => void;
    onAiHelpfulStateChange?: (questionIndex: number, isHelpful: -1 | 0 | 1) => Promise<void>;
    appServices?: AppServices | null;
    localizer: IStringLocalizer;
    fileList?: FileTableEntry[] | null | undefined;
    inventoryId?: string;
    responseId?: string;
    regulationId?: string;
    pageTitle: string;
    revisionInstructions?: string;
    language: string;
    isDisabled?: boolean;
    bomHasAtLeastOneIngredient: boolean;
    regulationLogicManager: BaseRegulationLogicManager;
    hasIngredientDisclosure?: boolean | undefined;
    supplierRequiredToDiscloseRegulatoryIngredients: boolean;
    navigateToBomPage: () => void;
    extensionRequested: boolean | undefined;
    plausibilityData: PlausibilityData[] | undefined;
    extensibleColumnNames: string[];
    visibleOverride?: boolean; // when supplied, this determines if anything is rendered, and other required/hidden logic is ignored
    requiredOverride?: boolean; // when supplied, this determines that any other required logic for displaying the red asterisk is ignored
    surveyState?: number; // ResponseStateType: 0=InProgress, 1=CompleteAndSubmitted, 2=Canceled (for conditional REACH logic)
    questionnaireProcessingType?: QuestionnaireProcessingType | null;
}

type QuestionProps = QuestionOwnProps & StateProps;

const Question: React.FC<QuestionProps> = ({ questionIndex, displayIndex, parentDisplayIndex, question, onResponseChange, onAiHelpfulStateChange, appServices, coreData, selectedInventory, localizer, fileList, inventoryId, responseId, regulationId, pageTitle, revisionInstructions, language, isDisabled, bomHasAtLeastOneIngredient, regulationLogicManager, hasIngredientDisclosure, supplierRequiredToDiscloseRegulatoryIngredients, navigateToBomPage, extensionRequested, extensibleColumnNames, plausibilityData, visibleOverride, requiredOverride, surveyState, questionnaireProcessingType }) => {
    const emptyGuid = "00000000-0000-0000-0000-000000000000";
    const stringType = "string";
    const trueAsBit = 1;
    const falseAsBit = 0;
    const toxnotServiceClient = new ToxnotServiceClient();
    const [currentFileIds, setCurrentFileIds] = useState(question.fileIds);
    const [allFiles, setAllFiles] = useState(fileList || []);
    const [isAiMetadataModified, setIsAiMetadataModified] = useState(false);

    // Keep allFiles in sync with fileList prop (fixes file disappearing after navigation)
    useEffect(() => {
        setAllFiles(fileList || []);
    }, [fileList]);

    // Keep currentFileIds in sync with question.fileIds prop
    useEffect(() => {
        setCurrentFileIds(question.fileIds);
    }, [question.fileIds]);

    useEffect(() => {
        question.fileIds = currentFileIds;
    }, [currentFileIds]);

    useEffect(() => {
        fileList = allFiles;
    }, [allFiles]);

    const onValueChange = (value: any, valueComment?: string, files?: FileTableEntry[], fileIds?: string[], index?: number) => {
        if (!onResponseChange) {
            return;
        }

        const filesToSave = files ?? allFiles;
        const valueToSave = typeof value === stringType ? value : JSON.stringify(value);

        onResponseChange(valueToSave, valueComment ?? "", index ?? questionIndex, filesToSave, fileIds ?? currentFileIds);
    }

    const onCsiDivisionChange = (value: string, secondValue: string) => {
        if (onResponseChange) {
            onResponseChange(`${value}:${secondValue}`, "", questionIndex, allFiles, currentFileIds);
        }
    }

    const onCheckboxValuesChange = (predefinedAnswers: QuestionnairePredefinedAnswer[], answers: string[]) => {
        if (!onResponseChange) {
            return;
        }

        answers = regulationLogicManager?.onCheckBoxResponseChange(question, answers) ?? answers;

        const responses = JSON.stringify(predefinedAnswers.map(p => answers.some(a => a == p.nativeAnswer) ? trueAsBit : falseAsBit));

        onResponseChange(responses, "", questionIndex, allFiles, currentFileIds);
    }

    const onFileDelete = (id: string) => {
        const deleteFile = (async () => {
            if (inventoryId) {
                await toxnotServiceClient.deleteInventoryFile(id, inventoryId);
            } else {
                await toxnotServiceClient.deleteGeneralFile(id);
            }

            const newFilesList = allFiles.filter(f => f.fileId != id);
            setAllFiles(newFilesList);

            const newFileIdsList = currentFileIds.filter(f => f != id);
            setCurrentFileIds(newFileIdsList);

            onValueChange(question.requireFileAttachment && question.type != QuestionType.File && question.type != QuestionType.ImportCMRT ? question.value : JSON.stringify(newFileIdsList), "", newFilesList, newFileIdsList);
        });

        return deleteFile();
    }

    const onFileUpload = (fileDetails: FileTableEntry, file: File, metadataTypeId: number, index?: number) => {
        const upload = (async () => {
            if (file) {
                const result = inventoryId ? await toxnotServiceClient.uploadInventoryFile(fileDetails.filename, fileDetails.metadata.description, question.summaryFieldKeyInventoryFileType ?? 1, true, true, file, metadataTypeId, fileDetails.inventoryId) :
                    await toxnotServiceClient.uploadGeneralFile(fileDetails.filename, fileDetails.metadata.description, question.summaryFieldKeyInventoryFileType ?? 1, true, true, file, metadataTypeId, fileDetails.inventoryId);

                if (result?.data?.fileId) {
                    const newFileId = result.data.fileId;
                    fileDetails.fileId = newFileId;

                    const newFilesList = allFiles.slice();
                    newFilesList.push(fileDetails);
                    setAllFiles(newFilesList);

                    const newFileIdsList = currentFileIds.slice();
                    newFileIdsList.push(newFileId);
                    setCurrentFileIds(newFileIdsList);

                    onValueChange(question.requireFileAttachment && question.type != QuestionType.File && question.type != QuestionType.ImportCMRT ? question.value : JSON.stringify(newFileIdsList), question.valueComment, newFilesList, newFileIdsList, index);
                }
            }

            return fileDetails;
        });

        return upload();
    }

    const onMetadataChange = (file: FileTableEntry) => {
        const updatedFiles = allFiles.filter(f => f.fileId != file.fileId);

        updatedFiles.push(file);
        setAllFiles(updatedFiles);
        onValueChange(question.requireFileAttachment && question.type != QuestionType.File ? question.value : JSON.stringify(currentFileIds), question.valueComment, updatedFiles)
    }

    const onSafeUseInstructionChange = (safeUseInstructions: string, noSafeUseInstructionRequired: boolean) => {
        if (!onResponseChange) {
            return;
        }

        const valueToSave: SafeUseInstructionsScip = {
            noSafeUseInstructionRequired,
            safeUseInstructions
        };

        onResponseChange(JSON.stringify(valueToSave), "", questionIndex, allFiles, currentFileIds);
    }

    const parseCsiDivision = (value: string): DeclareCsiDivision => {
        return {
            name: value.split(':')[0],
            subdivisions: [value.split(':')[1]]
        };
    }

    const parseToStringList = (value: string): string[] => {
        try {
            return JSON.parse(value) as string[];
        } catch {
            return [];
        }
    }

    const parseToCheckboxValues = (predefinedAnswers: QuestionnairePredefinedAnswer[], answer: string): string[] => {
        try {
            let answerList: number[] = JSON.parse(answer);

            return predefinedAnswers.filter((_, index) => answerList[index] === trueAsBit).map(a => a.nativeAnswer);
        } catch {
            return [];
        }
    }

    const extractMatchingPredefinedAnswers = (
        answer: string,
        predefinedAnswers: QuestionnairePredefinedAnswer[]
    ): string[] => {
        if (!answer || !answer.trim()) {
            return [];
        }

        let remainingText = answer;
        const matchedAnswers: string[] = [];
        const sortedPredefinedAnswers = [...predefinedAnswers]
            .map(a => a.nativeAnswer)
            .sort((a, b) => b.length - a.length);

        while (remainingText.trim()) {
            let foundMatch = false;

            for (const predefinedAnswer of sortedPredefinedAnswers) {
                if (matchedAnswers.some(a => a.toLowerCase() === predefinedAnswer.toLowerCase())) {
                    continue;
                }

                const index = remainingText.toLowerCase().indexOf(predefinedAnswer.toLowerCase());
                if (index === -1) {
                    continue;
                }

                matchedAnswers.push(predefinedAnswer);

                const beforeMatch = remainingText.slice(0, index).trimEnd().replace(/,$/, "").trimEnd();
                const afterMatch = remainingText.slice(index + predefinedAnswer.length).trimStart().replace(/^,/, "").trimStart();
                remainingText = beforeMatch
                    ? `${beforeMatch}${afterMatch ? `, ${afterMatch}` : ""}`
                    : afterMatch;

                foundMatch = true;
                break;
            }

            if (foundMatch) {
                continue;
            }

            const fallbackMatches = remainingText
                .split(",")
                .map(value => value.trim())
                .filter(Boolean);

            for (const fallbackMatch of fallbackMatches) {
                const matchedAnswer = predefinedAnswers.find(a => a.nativeAnswer.toLowerCase() === fallbackMatch.toLowerCase());
                if (matchedAnswer && !matchedAnswers.some(a => a.toLowerCase() === matchedAnswer.nativeAnswer.toLowerCase())) {
                    matchedAnswers.push(matchedAnswer.nativeAnswer);
                }
            }

            break;
        }

        return matchedAnswers;
    };

    const normalizeAiMultiCheckboxValue = (
        predefinedAnswers: QuestionnairePredefinedAnswer[],
        answer: string | null | undefined
    ): string => {
        const trimmedAnswer = answer?.trim() ?? "";
        if (!trimmedAnswer) {
            return "";
        }

        if (trimmedAnswer.startsWith("[") && trimmedAnswer.endsWith("]")) {
            return trimmedAnswer;
        }

        const matchedAnswers = extractMatchingPredefinedAnswers(trimmedAnswer, predefinedAnswers);
        return JSON.stringify(predefinedAnswers.map(p => matchedAnswers.some(a => a.toLowerCase() === p.nativeAnswer.toLowerCase()) ? trueAsBit : falseAsBit));
    };

    const parseToDataTable = (answer: string): string[][] => {
        try {
            return JSON.parse(answer);
        } catch {
            return [];
        }
    }

    const parseArticleIdentifierValue = (value: string): ArticleIdentifier => {
        try {
            return JSON.parse(value);
        } catch {
            return {
                value: "",
                typeOtherText: "",
                identifierTypeId: ""
            };
        }
    }

    const parceCertification = (value: string): Certification => {
        try {
            let certification: Certification = JSON.parse(value);
            return certification;
        } catch {
            return {
                name: "",
                certificationExternalId: "",
                type: CertificationType.Other,
                metadata: {
                    level: ""
                },
                expirationDate: new Date().toISOString()
            };
        }
    }

    const mapExistingFiles = (filesIds: string[]) => {
        let existingFiles: FileTableEntry[] = [];
        //Avoiding duplication in FileIDs 
        filesIds = filesIds.filter((item, index, self) => self.indexOf(item) === index);
        if (filesIds.length > 0) {
            filesIds.map(fileId => {
                const file = fileList?.firstOrDefault(f => f.fileId == fileId);

                if (file) {
                    if (file.metadataJson) {
                        file.metadata = JSON.parse(file.metadataJson);
                    }

                    if (typeof file.metadata == "string") {
                        file.metadata = JSON.parse(file.metadata);
                    }

                    existingFiles.push(file);
                }
            });
        }

        return existingFiles;
    }

    const mapReachListVersions = (value: string) => {
        const reachListVersions = ReachListVersions.reachListVersions.first(rv => rv.date == value);
        return `${reachListVersions.date} (${reachListVersions.count} ${localizer.localizeString("Substances")})`;
    }

    const getFileUploader = () => {
        return (
            <FileUploader
                dataSource={mapExistingFiles(question.fileIds)}
                uploadFileAction={onFileUpload}
                metadataChangeAction={onMetadataChange}
                deleteFileAction={onFileDelete}
                addButtonLocalised={localizer.localizeString("Add Document")}
                localizer={localizer}
                inventoryId={inventoryId}
                questionIndex={questionIndex}
                isDisabled={!!isDisabled}
            />
        );
    };

    const getFriendlyNameForOptions = (option: string) => {
        const predefined = question.predefinedAnswers.firstOrDefault(o => o.nativeAnswer == option);
        const localized =
            (predefined?.languageToAnswerMap[language] && localizer.localizeString(predefined.languageToAnswerMap[language])) ||
            (predefined?.nativeAnswer && localizer.localizeString(predefined.nativeAnswer)) ||
            (option && localizer.localizeString(option));
        return localized ||
            predefined?.languageToAnswerMap[language] ||
            predefined?.nativeAnswer ||
            option ||
            "";
    }

    const isRohsLegacyExemptionOption = (option: string) => {
        const optionIndex = question.predefinedAnswers.findIndex(answer => answer.nativeAnswer === option);
        const exemptionKey = optionIndex >= 0
            ? question.summaryFieldMultiCheckboxIndexToKeyList?.[optionIndex]
            : undefined;

        return (exemptionKey ? RohsExemptions.isLegacyCode(exemptionKey) : false) ||
            option.startsWith(RohsExemptions.expiredPrefix) || option.startsWith("(Expired)");
    }

    const hadFeedbackMessage = (question: QuestionnaireQuestion) => {
        return question.feedbackMessage?.answers?.some(answer => answer == question.value) ?? false;
    }

    const getIngredientBasedAnswerContext = (): IngredientBasedAnswerContext | null => {
        if (!regulationLogicManager) {
            return null;
        }

        if (!regulationLogicManager._questionRegulatoryData) {
            return null;
        }

        const regulatoryQuestionData = regulationLogicManager._questionRegulatoryData.find(q => q.applicableQuestionId === question.id);

        if (regulatoryQuestionData === undefined) {
            return null;
        }

        if (regulatoryQuestionData.regulationListIds.length === 0) {
            return null;
        }

        if (!regulationLogicManager.hasAtLeastOneCasrnPresent()) {
            return null;
        }

        let suggestedAnswer: string;
        let contextMessage: string;
        let hasNonCompliantIngredients: boolean;

        if (regulationLogicManager.isRegulatedSubstancePresent(regulatoryQuestionData)) {
            suggestedAnswer = regulatoryQuestionData.containsSubstancesResponse;
            contextMessage = localizer.localizeString("Suggested answer - your Ingredient Disclosure contains regulated substances");
            hasNonCompliantIngredients = true;
        }
        else {
            suggestedAnswer = regulatoryQuestionData.doesNotContainSubstancesResponse;
            contextMessage = localizer.localizeString("Suggested answer - your Ingredient Disclosure does not contain regulated substances");
            hasNonCompliantIngredients = false;
        }

        const originalContext: IngredientBasedAnswerContext = {
            defaultValueForUnanswered: suggestedAnswer,
            answerToLocalizedContextMessageMap: {
                [suggestedAnswer.toLowerCase()]: contextMessage
            },
            suggestedAnswer: suggestedAnswer,
            hasNonCompliantIngredients: hasNonCompliantIngredients
        };

        return applyAiSoftCheckDecision({
            originalContext,
            hasMatchingAiMetadata: shouldRenderAiAnswerMetadata,
            currentValue: currentValueForAiCompare,
            aiSuggestedValue: normalizedAiMetadataForDisplay?.value,
            buildWarningContext: (_, aiSuggestedAnswer) => {
                const normalizedAiSuggestedAnswer = normalizeSoftCheckAnswer(aiSuggestedAnswer);
                const normalizedContainsResponse = normalizeSoftCheckAnswer(regulatoryQuestionData.containsSubstancesResponse);
                const normalizedDoesNotContainResponse = normalizeSoftCheckAnswer(regulatoryQuestionData.doesNotContainSubstancesResponse);
                const aiSuggestsContainsSubstances = normalizedAiSuggestedAnswer === normalizedContainsResponse;
                const aiSuggestsDoesNotContainSubstances = normalizedAiSuggestedAnswer === normalizedDoesNotContainResponse;
                const warningMessage = aiSuggestsContainsSubstances
                    ? localizer.localizeString("Data Inconsistency Detected - your Ingredients Disclosure contains regulated substances.")
                    : aiSuggestsDoesNotContainSubstances
                        ? localizer.localizeString("Data Inconsistency Detected - your Ingredients Disclosure does not contains regulated substances.")
                        : contextMessage;

                return {
                    defaultValueForUnanswered: aiSuggestedAnswer,
                    answerToLocalizedContextMessageMap: {
                        [aiSuggestedAnswer.toLowerCase()]: warningMessage
                    },
                    suggestedAnswer: aiSuggestedAnswer,
                    onlyShowWhenAnswerConflictsWithSuggestion: true,
                    hasNonCompliantIngredients: aiSuggestsContainsSubstances
                        ? true
                        : aiSuggestsDoesNotContainSubstances
                            ? false
                            : hasNonCompliantIngredients
                };
            }
        });
    }

    const getPlausibilityAnswerContext = (): PlausibilityDataAnswerContext | null => {
        if (!plausibilityData) {
            return null;
        }

        let suggestedAnswer = "";
        let contextMessage = "";
        const plausibilityDataQuestions: QuestionnaireQuestion[] = [];

        if (question.summaryFieldKey === "rohs_subject_to_directive" || question.summaryFieldKey === "scip_reach_is_article") {
            plausibilityDataQuestions.push(question);
        }

        if (plausibilityDataQuestions.length > 0) {
            plausibilityDataQuestions.map(q => {
                if (q.summaryFieldKey === "rohs_subject_to_directive") {
                    plausibilityData.map(p => {
                        if (Number(p.confidence) >= 75) {
                            if (p.question === "Electronic") {
                                if (p.answer === "True") {
                                    suggestedAnswer = localizer.localizeString("Yes");
                                    contextMessage = localizer.localizeString("Suggested answer - this product may be an electronic.");
                                }
                                if (p.answer === "False") {
                                    suggestedAnswer = localizer.localizeString("No");
                                    contextMessage = localizer.localizeString("Suggested answer - this product may not be an electronic.");
                                }
                            }
                        }
                    });
                }
                if (q.summaryFieldKey === "scip_reach_is_article") {
                    plausibilityData.map(p => {
                        if (Number(p.confidence) >= 75) {
                            if (p.question === "Article") {
                                if (p.answer === "True") {
                                    suggestedAnswer = localizer.localizeString("Article as such or Complex Object");
                                    contextMessage = localizer.localizeString("Suggested answer - this product may be a REACH article");
                                }
                                if (p.answer === "False") {
                                    suggestedAnswer = localizer.localizeString("No - Not an Article");
                                    contextMessage = localizer.localizeString("Suggested answer - this product may not be a REACH article");
                                }
                            }
                        }
                    });
                }
            });
        }

        return {
            defaultValueForUnanswered: suggestedAnswer,
            suggestedAnswer: suggestedAnswer,
            answerToLocalizedContextMessageMap: {
                [suggestedAnswer]: contextMessage
            },
        }
    }

    const safeUseInstructionParse = (value: string): SafeUseInstructionsScip => {
        try {
            const parsed = JSON.parse(value) as unknown;
            if (parsed && typeof parsed === "object") {
                const safeUse = parsed as { noSafeUseInstructionRequired?: unknown; safeUseInstructions?: unknown };
                return {
                    noSafeUseInstructionRequired: safeUse.noSafeUseInstructionRequired === true,
                    safeUseInstructions: typeof safeUse.safeUseInstructions === "string" ? safeUse.safeUseInstructions : ""
                };
            }
        } catch {
        }

        return {
            noSafeUseInstructionRequired: false,
            safeUseInstructions: ""
        };
    }

    const toSafeUseCompareValue = (rawValue: string | null | undefined): string => {
        const parsed = safeUseInstructionParse((rawValue ?? "").trim());
        return parsed.noSafeUseInstructionRequired
            ? "true"
            : `false:${(parsed.safeUseInstructions ?? "").trim()}`;
    };

    const toSafeUseRestoreValue = (rawValue: string | null | undefined): string => {
        const parsed = safeUseInstructionParse((rawValue ?? "").trim());
        const valueToRestore: SafeUseInstructionsScip = {
            noSafeUseInstructionRequired: parsed.noSafeUseInstructionRequired,
            safeUseInstructions: (parsed.safeUseInstructions ?? "").trim()
        };

        return JSON.stringify(valueToRestore);
    };

    const linkedOriginalResponseInventoryId =
        inventoryId && selectedInventory?.revisionRequest != null
            ? (coreData?.response?.inventoryDataList
                ?.find(d => d.responderOwnedResponseInventoryId === inventoryId)
                ?.requestorOwnedResponseInventoryId ?? null)
            : null;
    const normalizedAiSourceInventoryId = (question.aiMetadata?.sourceInventoryId ?? "").toLowerCase();
    const normalizedInventoryId = (inventoryId ?? "").toLowerCase();
    const normalizedLinkedOriginalResponseInventoryId = (linkedOriginalResponseInventoryId ?? "").toLowerCase();
    const isSupplierQuestionnaire = !inventoryId;
    const allowedMaterialSourceInventoryIds = [
        normalizedInventoryId,
        ...(normalizedLinkedOriginalResponseInventoryId ? [normalizedLinkedOriginalResponseInventoryId] : [])
    ].filter(id => id.length > 0);
    const isMatchingSourceInventory = isSupplierQuestionnaire
        ? normalizedAiSourceInventoryId === emptyGuid
        : allowedMaterialSourceInventoryIds.includes(normalizedAiSourceInventoryId);

    const shouldRenderAiAnswerMetadata = !!question.aiMetadata
        && question.type !== QuestionType.Section
        && question.type !== QuestionType.Subsection
        && isMatchingSourceInventory;
    const isScipSafeUseQuestion = question.scipDataKey === ScipDataManager.ScipDataKeySafeUseInstructions
        && question.type === QuestionType.CheckboxAndComment;
    const shouldNormalizeJsonForAiCompare = question.type === QuestionType.DataTable
        || question.type === QuestionType.IngredientTable
        || question.type === QuestionType.ScipCharacteristics;
    const normalizeValueForAiCompare = (val: string): string => {
        if (isScipSafeUseQuestion) return toSafeUseCompareValue(val);
        if (question.type === QuestionType.MultiCheckbox) return normalizeAiMultiCheckboxValue(question.predefinedAnswers, val);
        if (shouldNormalizeJsonForAiCompare) return normalizeJsonStringForComparison(val);
        return val;
    };
    const normalizedAiMetadataForDisplay = question.aiMetadata
        ? { ...question.aiMetadata, value: normalizeValueForAiCompare(question.aiMetadata.value) ?? question.aiMetadata.value }
        : question.aiMetadata;
    const currentValueForAiCompare = normalizeValueForAiCompare(question.value) ?? '';
    const isImportCmrtQuestion = question.type === QuestionType.ImportCMRT;
    const shouldShowAiModifiedState = question.type !== QuestionType.File
        && question.type !== QuestionType.ImportCMRT;

    useEffect(() => {
        if (!shouldRenderAiAnswerMetadata || !shouldShowAiModifiedState) {
            setIsAiMetadataModified(false);
        }
    }, [shouldRenderAiAnswerMetadata, shouldShowAiModifiedState]);

    const onRevertToAiAnswer = () => {
        let aiValueToRestore = normalizedAiMetadataForDisplay?.value;

        if (aiValueToRestore === null || aiValueToRestore === undefined) {
            const telemetryContext = {
                questionId: question.id,
                questionType: question.type
            };
            appServices?.trackEvent("survey.on_revert_to_ai_answer", telemetryContext);
            return;
        }

        if (isScipSafeUseQuestion) {
            aiValueToRestore = toSafeUseRestoreValue(aiValueToRestore);
        }

        if (question.type === QuestionType.MultiCheckbox) {
            onCheckboxValuesChange(question.predefinedAnswers, parseToCheckboxValues(question.predefinedAnswers, aiValueToRestore));
            return;
        }

        onValueChange(aiValueToRestore, question.valueComment);
    };

    const onAiHelpfulSelectionChange = (isHelpful: -1 | 0 | 1) => {
        if (!responseId) {
            return;
        }

        const fieldText = (question.text ?? "").trim();
        if (fieldText.length === 0) {
            return;
        }

        if (!questionnaireProcessingType) {
            return;
        }

        var summaryKey = (question.summaryFieldKey ?? "").trim();
        if (questionnaireProcessingType === QuestionnaireProcessingType.Legacy && (question.type === QuestionType.File || question.type === QuestionType.ImportCMRT) && summaryKey.length > 0 && !summaryKey.endsWith("_count")) {
            summaryKey = summaryKey.length > 0 ? `${summaryKey}_count` : "";
        }

        const normalizedPageName = (pageTitle ?? "").trim();
        const normalizedScipDataKey = (question.scipDataKey ?? "").trim();

        if (onAiHelpfulStateChange) {
            onAiHelpfulStateChange(questionIndex, isHelpful);
        }

        void (async () => {
            const result = await toxnotServiceClient.aiQuestionHelpfulFeedback({
                responseId,
                processingType: questionnaireProcessingType,
                inventoryId: inventoryId ?? null,
                pageName: normalizedPageName.length > 0 ? normalizedPageName : null,
                fieldText,
                summaryFieldKey: summaryKey.length > 0 ? summaryKey : null,
                scipDataKey: normalizedScipDataKey.length > 0 ? normalizedScipDataKey : null,
                isHelpful
            });

            if (!result.success) {
                const telemetryContext = {
                    apiResult: result.result,
                    responseId,
                    inventoryId,
                    questionnaireProcessingType,
                    pageName: normalizedPageName,
                    fieldText,
                    isHelpful
                };

                appServices?.trackEvent("survey.ai_helpful_feedback_submit_failed", telemetryContext);
            }
        })();
    };

    const determineAlertHeader = () => {
        const applicableQuestions = regulationLogicManager._questionRegulatoryData?.filter(q => q.applicableQuestionId === question.id) ?? [];

        const shouldShowAlertHeader = (hasIngredientDisclosure &&
            !regulationLogicManager.hasAtLeastOneCasrnPresent() &&
            applicableQuestions.length > 0)

        let alertHeader: JSX.Element | undefined = undefined;

        if (!shouldShowAlertHeader) {
            return alertHeader;
        }

        const messageAndClasses = {
            message: <></>,
            classNames: ""
        };

        if (supplierRequiredToDiscloseRegulatoryIngredients && (applicableQuestions[0].containsSubstancesResponse.toLowerCase() === question.value.toLowerCase())) {
            messageAndClasses.message = <>{localizer.localizeString("Please update the Ingredient Disclosure list with the corresponding ingredient.")} <a onClick={navigateToBomPage}>{localizer.localizeString("Update Ingredient Disclosure")}</a></>;
            messageAndClasses.classNames = "alert alert-danger";
        } else {
            messageAndClasses.message = <>{localizer.localizeString("There are no ingredients defined in your Ingredient Disclosure.")}</>;
            messageAndClasses.classNames = "alert alert-warning";
        }

        alertHeader = <div className={messageAndClasses.classNames} style={{ padding: "4px", border: "none", marginBottom: "0" }}>{messageAndClasses.message}</div>

        return alertHeader;
    }

    const getQuestionBasedOnType = () => {
        const isRohsExemptionsQuestion = question.summaryFieldKey === "rohs_exemptions";

        // Check if the summaryFieldKey is cmrt_declaration_of_scope_or_class
        if (question.summaryFieldKey === "cmrt_declaration_of_scope_or_class" && question.value === "Custom") {
            // Interpret "Custom" as "User Defined"
            question.value = "User Defined";
        }

        switch (question.type) {
            case QuestionType.Text:
                return <TextInput value={question.value}
                    onValueChange={onValueChange}
                    idPrefix={question.id} />

            case QuestionType.MultipleDropdown:
                return <DropdownMenu options={["--", ...question.predefinedAnswers.map(a => a.nativeAnswer)]}
                    onOptionSelected={onValueChange}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    value={question.value}
                    getUniqueValueForOption={o => o}
                />

            case QuestionType.Comment:
                return <TextAreaInput value={question.value}
                    onValueChange={onValueChange} />

            case QuestionType.ImageUrl:
            case QuestionType.Url:
                return <UrlInput value={question.value}
                    onValueChange={onValueChange} />

            case QuestionType.Datetime:
                return <DateInput value={question.value}
                    onValueChange={onValueChange} />

            case QuestionType.ScipArticleCategory:
                return <ScipCategory onListChange={onValueChange}
                    localizer={localizer}
                    existingCategories={parseToStringList(question.value)}
                    categoryType={CategoryType.Article}
                    buttonText={localizer.localizeString("Add EU ECHA Article Category")}
                />

            case QuestionType.ScipCharacteristics:
                return <Characteristics localizer={localizer} onValueChanged={onValueChange} value={question.value} />

            case QuestionType.MultiText:
                return <InputList addNewFieldText={localizer.localizeString("Add New Answer")}
                    dataSource={parseToStringList(question.value)}
                    onListItemsChange={onValueChange} />

            case QuestionType.CsiDivision:
                const csiDivision = parseCsiDivision(question.value);
                return <CsiDivision options={DeclareCsiDivisionsManager.declareCsiDivisions}
                    selectedMainDivision={csiDivision.name}
                    selectedSubdivision={csiDivision.subdivisions[0]}
                    onOptionChange={onCsiDivisionChange} />

            case QuestionType.Multiple:
                const alertHeader = determineAlertHeader();

                return <RadioButtons
                    options={question.predefinedAnswers.map(a => a.nativeAnswer)}
                    value={question.value !== ResourcesConstants.NativeAnswerTexts.NoResponse ? question.value : ""}
                    valueComment={question.valueComment ?? ""}
                    ingredientBasedAnswerContext={getIngredientBasedAnswerContext()}
                    plausibilityBasedAnswerContext={getPlausibilityAnswerContext()}
                    onValueChange={onValueChange}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    isDisabled={isDisabled}
                    localizer={localizer}
                    questionAlertHeader={alertHeader}
                />

            case QuestionType.MultiCheckbox:
            case QuestionType.Attestation:
                return <CheckBoxes options={question.predefinedAnswers.map(a => a.nativeAnswer)}
                    values={parseToCheckboxValues(question.predefinedAnswers, question.value)}
                    onValueChange={(value: string[]) => onCheckboxValuesChange(question.predefinedAnswers, value)}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    isDisabled={isDisabled}
                    disabledValues={question.disabledValues}
                    enableLegacyToggle={isRohsExemptionsQuestion}
                    legacyToggleLabel={localizer.localizeString("Show Legacy / Expired Exemptions")}
                    legacyOptionPrefix={RohsExemptions.expiredPrefix}
                    isLegacyOption={isRohsExemptionsQuestion ? isRohsLegacyExemptionOption : undefined} />

            case QuestionType.DataTable:
                if (question.subType === null || question.subType.trim() === '') {
                    return <DataTable columns={question.predefinedAnswers.map(a => a.languageToAnswerMap[language] ?? a.nativeAnswer)}
                        rows={parseToDataTable(question.value)}
                        onResponseChange={onValueChange}
                        localizer={localizer}
                        radioColumns={regulationLogicManager.getRadioColumns()}
                    />
                }

                switch (question.subType) {
                    case QuestionSubType.FCN:
                        const FCNDataTableColumns = CustomDataTableColumnProvider.getFCNDataTableColumns(localizer);

                        return <CustomDataTable columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            rows={parseToDataTable(question.value)}
                            onResponseChange={onValueChange}
                            customColumns={FCNDataTableColumns}
                            localizer={localizer}
                            index={questionIndex}
                            addButtonText={localizer.localizeString("Add New Row")}
                        />

                    case QuestionSubType.FDA:
                        return <FDACitationsDataTable columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            rows={parseToDataTable(question.value)}
                            onResponseChange={onValueChange}
                            localizer={localizer}
                        />

                    case QuestionSubType.Polymer:
                        const polymerDataTableColumns = CustomDataTableColumnProvider.getPolymerDataTableColumns(localizer);

                        return <CustomDataTable columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            rows={parseToDataTable(question.value)}
                            onResponseChange={onValueChange}
                            customColumns={polymerDataTableColumns}
                            localizer={localizer}
                            index={questionIndex}
                            addButtonText={localizer.localizeString("Add New Row")}
                        />

                    case QuestionSubType.Packaging:
                        return <PackagingDataTable localizer={localizer}
                            columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            onResponseChange={onValueChange}
                            rows={parseToDataTable(question.value)}
                        />

                    case QuestionSubType.PrintingTechnique:
                        const printingTechniqueDataTableColumns = CustomDataTableColumnProvider.getPrintingTechniqueDataTableColumns(localizer);

                        return <CustomDataTable columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            rows={parseToDataTable(question.value)}
                            onResponseChange={onValueChange}
                            customColumns={printingTechniqueDataTableColumns}
                            localizer={localizer}
                            index={questionIndex}
                            addButtonText={localizer.localizeString("Add New Row")}
                        />

                    case QuestionSubType.ProductionSite:
                        const productionSiteDataTableColumns = CustomDataTableColumnProvider.getProductionSiteDataTableColumns(localizer);

                        return <CustomDataTable columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                            rows={parseToDataTable(question.value)}
                            onResponseChange={onValueChange}
                            customColumns={productionSiteDataTableColumns}
                            localizer={localizer}
                            index={questionIndex}
                            addButtonText={localizer.localizeString("Add New Row")}
                        />

                    default:
                        console.log(`Unknown question sub type: ${question.subType}`);
                        return null;
                }

            case QuestionType.ScipArticleIdentifier:
                const articleIdentifier = parseArticleIdentifierValue(question.value);

                return <ScipArticleIdentifier value={articleIdentifier}
                    onValueChange={onValueChange}
                    localizer={localizer}
                    idPrefix={`scip-article-identifier-${displayIndex}`} />

            case QuestionType.IngredientTable:
                const columnsFriendlyNames = (new IngredientsTableColumnManager()).getIngredientsColumnFriendlyNames(localizer, extensibleColumnNames);

                return <IngredientsTable
                    columns={question.predefinedAnswers.map(a => a.nativeAnswer)}
                    rows={parseToDataTable(question.value)}
                    localizedFriendlyNameMap={columnsFriendlyNames}
                    onResponseChange={onValueChange} />

            case QuestionType.File:
                return getFileUploader();

            // Inside your switch statement or render function
            case QuestionType.ImportCMRT:
                return (
                    <ImportCMRT
                        localizer={localizer}
                        inventoryId={inventoryId}
                        questionIndex={questionIndex}
                        dataSource={mapExistingFiles(question.fileIds)}
                        onFileUpload={onFileUpload}
                        onMetadataChange={onMetadataChange}
                        onFileDelete={onFileDelete}
                        onResponseChange={onValueChange}
                    />
                );

            case QuestionType.ComplianceResponseDate:
                return <ComplianceResponseDate value={question.value}
                    onValueChange={onValueChange}
                    localizer={localizer}
                    isDisabled={isDisabled} />

            case QuestionType.ComplianceResponseDateReach:
                return <ComplianceResponseDateReach
                    value={question.value}
                    onValueChange={onValueChange}
                    localizer={localizer}
                    isDisabled={isDisabled}
                    surveyState={surveyState} />

            case QuestionType.Certification:
                return <CertificationInput response={parceCertification(question.value)}
                    onResponseChange={onValueChange}
                    localizer={localizer} />

            case QuestionType.CheckboxAndComment:
                const safeInstruction = safeUseInstructionParse(question.value);
                return <CheckboxAndComment checkboxText={localizer.localizeString(question.predefinedAnswers[0].nativeAnswer)}
                    checked={safeInstruction.noSafeUseInstructionRequired}
                    comment={safeInstruction.safeUseInstructions ?? ""}
                    hideCommentOnlyOnValues={true}
                    onValueChange={onSafeUseInstructionChange}
                    isDisabled={isDisabled} />

            case QuestionType.CmrtDetails:
                return <CmrtDetails value={question.value}
                    onValueChange={onValueChange}
                    idPrefix={question.id}
                    fileList={fileList}
                    localizer={localizer}
                    inventoryId={inventoryId}
                    disabled={isDisabled}
                />
            case QuestionType.CmrtMultiMaterialInput:
                return <ConflictMaterialsMultiMaterialInput
                    idPrefix={question.id}
                    isDisabled={isDisabled}
                    value={question.value}
                    localizer={localizer}
                    onValueChanged={onValueChange}
                />

            case QuestionType.Subsection:
                return "";

            case QuestionType.Section:
                return "";

            case QuestionType.DropdownMenuAndComment:
                return <DropdownMenuAndComment options={["Choose One", ...question.predefinedAnswers.map(a => a.nativeAnswer)]}
                    localizer={localizer}
                    onValueChange={onValueChange}
                    getFriendlyNameForOptions={getFriendlyNameForOptions}
                    value={question.value}
                    getUniqueValueForOption={o => o}
                    isDisabled={isDisabled}
                    label="Answer" />
            case QuestionType.CMRTMineralsComponent:
                return (
                    <CMRTMineralsComponent
                        options={["Choose One", ...question.predefinedAnswers.map(a => a.nativeAnswer)]}
                        localizer={localizer}
                        value={question.value}
                        getFriendlyNameForOptions={getFriendlyNameForOptions}
                        getUniqueValueForOption={(o) => o}
                        isDisabled={isDisabled}
                        labels={["Tantalum (Ta)", "Tin (Sn)", "Gold (Au)", "Tungsten (W)"]}
                        onValueChange={onValueChange}
                    />
                );
            case QuestionType.PhoneContact:
                return (
                    <PhoneContact
                        localizer={localizer}
                        value={question.value}
                        onValueChange={onValueChange}
                        isDisabled={isDisabled}
                    />
                );
            case QuestionType.SmelterList:
                return (
                    <SmelterList
                        value={question.value}
                        localizer={localizer}
                        onValueChange={onValueChange}
                        isDisabled={isDisabled}
                    />
                );
            case QuestionType.ProductList:
                return (
                    <ProductList
                        value={question.value}
                        localizer={localizer}
                        onValueChange={onValueChange}
                    />
                );

            default:
                console.log(`Unknown question type: ${question.type}`);
                return null;
        }
    }

    const instructionsToUse = question.languageToInstructionsMap[language] || localizer.localizeString(question.instructions);

    // TODO: there's a bug where hidden doesn't actually exist on the object, so default missing property to false for demo
    const questionHidden = visibleOverride === undefined
        ? question.hidden ?? false
        : !visibleOverride;

    return (
        <div>
            {!questionHidden &&
                <div className="question">
                    <div className="question-text">
                        {question.type === QuestionType.Section &&
                            <h4><strong>{question.languageToTextMap[language] ?? localizer.localizeString(question.text)}</strong></h4>
                        }
                        <p>
                            {question.type === QuestionType.Subsection &&
                                <span className="page-title-border flex-space-between">
                                    <span className="m-t-10 m-b-10 h4"> {question.languageToTextMap[language] ?? localizer.localizeString(question.text)}</span>
                                </span> 
                            }
                            {question.type !== QuestionType.Subsection && question.type !== QuestionType.Section && question.type !== QuestionType.SmelterList && question.type !== QuestionType.ProductList &&
                                <strong>{displayIndex ? displayIndex + 1 : 1}. {question.languageToTextMap[language] ?? localizer.localizeString(question.text) + (question.dynamicField)}</strong>
                            }
                            {(requiredOverride || (regulationLogicManager.isQuestionRequired(question) && !extensionRequested)) &&
                                <span className="p-relative m-l-5">
                                    <span className="text-danger p-absolute required-asterisk">*</span>
                                </span>
                            }
                        </p>
                        {/*{shouldDisplayIngredientsAlert() &&*/}
                        {/*    <IngredientsAlert ingredientsAlertModel={getIngredientsAlert(question)} />*/}
                        {/*}*/}
                        <RevisionRequest revisionRequestInstructions={localizer.localizeString(revisionInstructions ?? "")} revisionRequestTitle={localizer.localizeString("Revision Requested:")} localizer={localizer} extensibleColumnNames={extensibleColumnNames} />
                        {instructionsToUse &&
                            <div className="panel panel-default question-instructions-panel">
                                <div className="panel-body">
                                    <MarkdownRenderer    htmlText={localizer.localizeString(instructionsToUse)} />
                                </div>
                            </div>
                        }
                        {shouldRenderAiAnswerMetadata &&
                            <AIAnswerMetadata
                                localizer={localizer}
                                aiMetadata={normalizedAiMetadataForDisplay!}
                                currentValue={currentValueForAiCompare}
                                isDisabled={!!isDisabled}
                                shouldShowModifiedState={isImportCmrtQuestion ? false : shouldShowAiModifiedState}
                                shouldShowRevertButton={isImportCmrtQuestion ? false : shouldShowAiModifiedState}
                                containerClassName={isImportCmrtQuestion ? "cmrt-ai-suggested" : undefined}
                                detailsTopMessage={isImportCmrtQuestion ? {
                                    message: localizer.localizeString("The following CMRT file was suggested by our AI-Powered Document Analysis. Your CMRT answers on this page were extracted from this CMRT file.")
                                } : undefined}
                                onRevertToAiValue={(question.type === QuestionType.File || isImportCmrtQuestion) ? undefined : onRevertToAiAnswer}
                                onHelpfulSelectionChange={onAiHelpfulSelectionChange}
                                onModifiedStateChange={setIsAiMetadataModified}
                            />
                        }
                    </div>
                    <div className={isAiMetadataModified ? "question-response m-l-15 question-response--ai-modified" : "question-response m-l-15"}>
                        {getQuestionBasedOnType()}
                    </div>
                    <div className="m-t-5">
                        {((question.type != QuestionType.File) && question.requireFileAttachment) && getFileUploader()}
                    </div>
                    <div className="feedback-message">
                        {hadFeedbackMessage(question) &&
                            <div className="panel panel-default m-l-15">
                                <div className="panel-body">
                                    <MarkdownRenderer htmlText={question.languageToTextMap[language] ?? localizer.localizeString(question.feedbackMessage.text)} />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    coreData: state.surveyResponseState?.coreData ?? null,
    selectedInventory: state.surveyResponseState?.selectedInventory ?? null
});

export default connect(mapStateToProps)(Question);
